import React, { useEffect, useRef, useState } from "react";
import { Button, Input, message, Spin, Typography } from "antd";
import { LinkOutlined, UploadOutlined } from "@ant-design/icons";

import { uploadAdminImageAPI } from "../api/api";

const { Text } = Typography;

/**
 * Dual-mode image field: paste a URL or upload a file to S3.
 * Renders a thumbnail preview whenever a URL is present.
 *
 * Props (compatible with Ant Design Form.Item value/onChange):
 *   value           – current URL string (S3 URL stored in form)
 *   onChange        – callback(newUrl: string)
 *   onPreviewChange – optional callback(localBlobUrl|null) — fires with a
 *                     local blob URL right after file selection so parent
 *                     preview panels can show the image without needing S3
 *                     public access.
 *   placeholder     – URL input placeholder text
 *   accept          – file input accept attribute (default: image/*)
 */
const ImageUploadField = ({
  value,
  onChange,
  onPreviewChange,
  placeholder = "https://...",
  accept = "image/jpeg,image/png,image/svg+xml,image/webp,image/gif"
}) => {
  const [mode, setMode] = useState("url");
  const [uploading, setUploading] = useState(false);
  // Local blob URL for immediate, auth-free thumbnail preview
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const prevObjectUrlRef = useRef(null);

  // Revoke the old object URL when the component unmounts to avoid memory leaks
  useEffect(() => {
    return () => {
      if (prevObjectUrlRef.current) {
        URL.revokeObjectURL(prevObjectUrlRef.current);
      }
    };
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Reset input so the same file can be re-selected if needed
    e.target.value = null;

    // Revoke previous blob and create a new local preview URL immediately
    if (prevObjectUrlRef.current) {
      URL.revokeObjectURL(prevObjectUrlRef.current);
    }
    const blobUrl = URL.createObjectURL(file);
    prevObjectUrlRef.current = blobUrl;
    setLocalPreviewUrl(blobUrl);
    if (onPreviewChange) onPreviewChange(blobUrl);

    setUploading(true);
    try {
      const response = await uploadAdminImageAPI(file);
      const url = response.data.url;
      onChange(url);
      message.success("Image uploaded successfully");
    } catch {
      // Revert preview on failure
      setLocalPreviewUrl(null);
      if (onPreviewChange) onPreviewChange(null);
      message.error("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <Button
          size="small"
          type={mode === "url" ? "primary" : "default"}
          icon={<LinkOutlined />}
          onClick={() => setMode("url")}
        >
          URL
        </Button>
        <Button
          size="small"
          type={mode === "upload" ? "primary" : "default"}
          icon={<UploadOutlined />}
          onClick={() => setMode("upload")}
        >
          Upload
        </Button>
      </div>

      {/* URL input */}
      {mode === "url" && (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}

      {/* File upload */}
      {mode === "upload" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Spin spinning={uploading}>
            <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? "Uploading…" : "Choose File"}
            </Button>
          </Spin>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          {value && !uploading && (
            <Text type="success" style={{ fontSize: 12 }}>
              ✓ Uploaded
            </Text>
          )}
        </div>
      )}

      {/* Preview thumbnail — prefers local blob so private S3 URLs still display */}
      {(localPreviewUrl || value) && (
        <div style={{ marginTop: 10 }}>
          <img
            src={localPreviewUrl || value}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: 90,
              objectFit: "contain",
              borderRadius: 6,
              border: "1px solid #f0f0f0",
              background: "#fafafa",
              padding: 4
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
      )}
    </div>
  );
};

export { ImageUploadField };
